'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import cardLighting from '@/assets/imgs/card-lighting.webp';
import companiesEllipse from '@/assets/illustrations/companies-ellipse-app.svg';
import { studioClient } from '@/sanity/lib/client';
import {
  localizedMainCompaniesQuery,
  localizedEllipseCompaniesQuery,
} from '@/sanity/queries/companies';
import { Company } from '@/types/company';

import { Button } from '@/shared';

// Reusable Tag component function
const Tag = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div
    className={className}
    style={{
      width: 'fit-content',
      borderRadius: '14.67px',
      padding: '11.667px 16.333px',
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.00) 100%), rgba(60, 229, 133, 0.19)',
      boxShadow: '0 -2.33px 4px 0 rgba(4, 178, 74, 0.40) inset',
      color: '#FFFFFF',
      whiteSpace: 'nowrap',
    }}
    role="listitem">
    {children}
  </div>
);

// Reusable TagGrid component function
const TagGrid = ({ tags }: { tags: string[] }) => {
  return (
    <div className="flex flex-wrap justify-center gap-2" role="list">
      {tags.map((tag, index) => (
        <Tag className="" key={index}>
          {tag}
        </Tag>
      ))}
    </div>
  );
};

// Reusable CompanyCard component function
const CompanyCard = ({
  company,
  showButton = true,
}: {
  company: Company;
  showButton?: boolean;
}) => {
  const t = useTranslations('companies');
  return (
    <article
      className="-[rgba(67,255,101,0.80)] relative overflow-hidden rounded-t-[2.34375rem]"
      style={{
        background: 'linear-gradient(180deg, #04471C 0%, rgba(13, 3, 22, 0.00) 81.17%)',
        minHeight: '34rem',
        height: 'auto',
        width: '100%',
        maxWidth: '37.25rem',
      }}>
      <Image
        src={cardLighting}
        alt=""
        className="absolute top-0 left-0 h-full w-full object-cover"
        priority={false}
        loading="lazy"
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 37.25rem"
        aria-hidden="true"
      />

      <div className="relative z-10 flex flex-col items-center justify-center gap-4 p-4 sm:p-6 lg:p-10">
        <div className="h-full w-full">
          <Image
            src={company.logo.asset.url}
            alt={company.logo.alt}
            className="h-full w-full object-contain"
            priority={true}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 25vw, 37.25rem"
            width={company.logo.asset.metadata.dimensions.width}
            height={company.logo.asset.metadata.dimensions.height}
          />
        </div>
        <h3 className="mb-4 text-center text-xl font-medium text-white sm:text-2xl lg:text-5xl">
          {company.title}
        </h3>
        <p className="max-w-xs text-center text-xl font-medium text-[#D2D7E0] sm:max-w-sm sm:text-base lg:max-w-md">
          {company.description}
        </p>
        <div>
          <p className="py-3 text-center text-sm font-medium text-[#63B588]">{t('keyServices')}</p>
          {company.keyServices && company.keyServices.length > 0 && (
            <TagGrid tags={company.keyServices} />
          )}
        </div>

        {showButton && (
          <div className="mt-4 flex justify-center sm:mt-6 lg:mt-10">
            <Link href={company.websiteUrl} aria-label={`Visit ${company.title} website`}>
              <Button className="hover:bg-opacity-80 px-2 py-1 text-xs font-medium transition-colors focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none sm:px-3 sm:py-1.5 sm:text-sm lg:px-6 lg:py-3 lg:text-base">
                {t('visitWebsite')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </article>
  );
};

// Reusable EllipseCompanyCard component function for the Petronil-style layout
const EllipseCompanyCard = ({
  company,
  showButton = true,
}: {
  company: Company;
  showButton?: boolean;
}) => {
  const t = useTranslations('companies');
  return (
    <div className="relative z-10 mt-14 flex flex-col items-center justify-center gap-6 lg:flex-row lg:gap-10">
      <div className="h-64 w-64 sm:h-80 sm:w-80 lg:h-[400px] lg:w-[400px]">
        <div className="relative h-full w-full">
          <Image
            src={companiesEllipse}
            alt="Ellipse background"
            className="absolute h-full w-full"
            priority={false}
            loading="lazy"
          />
          <Image
            src={company.logo.asset.url}
            alt={company.logo.alt}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 object-contain"
            priority={false}
            loading="lazy"
            width={company.logo.asset.metadata.dimensions.width}
            height={company.logo.asset.metadata.dimensions.height}
          />
        </div>
      </div>

      <div className="flex max-w-[750px] flex-col gap-4 text-center lg:text-left">
        <h3 className="text-start text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
          {company.title}
        </h3>
        <p className="max-w-sm text-start text-base font-medium text-[#D2D7E0] lg:max-w-none lg:text-xl">
          {company.description}
        </p>

        {showButton && (
          <div className="flex justify-start">
            <Link href={company.websiteUrl} aria-label={`Visit ${company.title} website`}>
              <Button className="hover:bg-opacity-80 px-2 py-1 text-xs font-medium transition-colors focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none sm:px-3 sm:py-1.5 sm:text-sm lg:px-6 lg:py-3 lg:text-base">
                {t('visitWebsite')}
              </Button>
            </Link>
          </div>
        )}

        {company.keyServices && company.keyServices.length > 0 && (
          <div className="mt-6">
            <h4 className="py-3 text-start text-sm font-medium text-[#63B588]">
              {t('keyServices')}
            </h4>
            <div className="flex flex-wrap justify-start gap-2" role="list">
              {company.keyServices.map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable Background Blur Component
const BackgroundBlur = ({
  position,
}: {
  position:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'center-left'
    | 'center-right';
}) => {
  const getPositionStyles = () => {
    switch (position) {
      case 'top-left':
        return { left: '-40%', top: '0%' };
      case 'top-right':
        return { right: '-40%', top: '0%' };
      case 'bottom-left':
        return { left: '-40%', bottom: '-10%' };
      case 'bottom-right':
        return { right: '-40%', bottom: '-10%' };
      case 'center-left':
        return { left: '-40%', top: '50%', transform: 'translateY(-50%)' };
      case 'center-right':
        return { right: '-40%', top: '50%', transform: 'translateY(-50%)' };
      default:
        return { left: '-40%', top: '0%' };
    }
  };

  return (
    <div
      className="hidden lg:block"
      style={{
        position: 'absolute',
        width: '629.25px',
        height: '629.25px',
        borderRadius: '629.25px',
        opacity: 0.2,
        background: '#20FF24',
        filter: 'blur(161.04000854492188px)',
        zIndex: 1,
        pointerEvents: 'none',
        ...getPositionStyles(),
      }}
      aria-hidden="true"
    />
  );
};

export default function Companies() {
  const [mainCompanies, setMainCompanies] = React.useState<Company[]>([]);
  const [ellipseCompanies, setEllipseCompanies] = React.useState<Company[]>([]);
  const [loading, setLoading] = React.useState(true);
  const t = useTranslations('companies');
  const locale = useLocale();

  React.useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const [mainData, ellipseData] = await Promise.all([
          studioClient.fetch<Company[]>(localizedMainCompaniesQuery(locale)),
          studioClient.fetch<Company[]>(localizedEllipseCompaniesQuery(locale)),
        ]);

        setMainCompanies(mainData);
        setEllipseCompanies(ellipseData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching companies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [locale]);

  if (loading) {
    return (
      <section className="max-w-laptop relative mx-auto mt-11 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-20">
          <div className="text-lg text-white">{t('loadingCompanies')}</div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="max-w-laptop relative mx-auto mt-11 mb-5 px-4 sm:px-6 lg:px-8"
      aria-labelledby="companies-heading">
      {/* Background Blur Effects */}
      <BackgroundBlur position="top-left" />
      <BackgroundBlur position="top-right" />
      <BackgroundBlur position="center-left" />
      <BackgroundBlur position="center-right" />
      <BackgroundBlur position="bottom-left" />
      <BackgroundBlur position="bottom-right" />

      {/* Main Companies Section */}
      {mainCompanies.length > 0 && (
        <div className="relative z-10 flex flex-col items-start justify-center gap-10 lg:flex-row lg:gap-10">
          {mainCompanies.map((company) => (
            <CompanyCard key={company._id} company={company} showButton={true} />
          ))}
        </div>
      )}

      {/* Ellipse Companies Section */}
      {ellipseCompanies.map((company) => (
        <EllipseCompanyCard key={company._id} company={company} showButton={true} />
      ))}
    </section>
  );
}
